import React from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { ApiConfigSectionProps } from './types';
import ApiConfigValidator from '../ApiConfigValidator';

function ApiConfigSection({
  formData,
  setFormData,
  isEditing,
  validationResult,
  setValidationResult,
  showApiTypeDialog,
  setShowApiTypeDialog,
  handleApiTypeChange
}: ApiConfigSectionProps) {
  return (
    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">API Configuration</h3>
      
      {/* API Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          API Type <span className="text-red-500">*</span>
        </label>
        
        {isEditing ? (
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Current API Type:</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                {formData.api_type === 'cps' ? 'CPS Golf' : 'ChronoGolf'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowApiTypeDialog(true)}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 hover:border-blue-400 rounded-md transition-colors"
            >
              Change API Type
            </button>
          </div>
        ) : (
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="api_type"
                value="cps"
                checked={formData.api_type === 'cps'}
                onChange={(e) => handleApiTypeChange(e.target.value as 'cps' | 'chronogolf')}
                className="mr-2"
              />
              CPS Golf
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="api_type"
                value="chronogolf"
                checked={formData.api_type === 'chronogolf'}
                onChange={(e) => handleApiTypeChange(e.target.value as 'cps' | 'chronogolf')}
                className="mr-2"
              />
              ChronoGolf
            </label>
          </div>
        )}
      </div>

      {/* API Course ID - Universal field for all API types */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          API Course ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.api_course_id}
          onChange={(e) => setFormData(prev => ({ ...prev, api_course_id: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="1001"
          required
        />
        <p className="text-sm text-gray-500 mt-1">
          The unique identifier for this golf course in the API system
        </p>
      </div>

      {/* CPS API Configuration */}
      {formData.api_type === 'cps' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.api_config.baseUrl || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  api_config: { ...prev.api_config, baseUrl: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://example.cps.golf/onlineres/onlineapi/api/v1/onlinereservation"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referer URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.api_config.referer || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  api_config: { ...prev.api_config, referer: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://example.cps.golf/onlineresweb/search-teetime"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.api_config.siteId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  api_config: { ...prev.api_config, siteId: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="1"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.api_config.websiteId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  api_config: { ...prev.api_config, websiteId: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="68ffa70d-4c31-4511-f6c6-08db3507e474"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* ChronoGolf API Configuration */}
      {formData.api_type === 'chronogolf' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.api_config.baseUrl || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  api_config: { ...prev.api_config, baseUrl: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://www.chronogolf.com/marketplace/v2/teetimes"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Club URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.api_config.clubUrl || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  api_config: { ...prev.api_config, clubUrl: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://www.chronogolf.com/club/country-meadows-golf-club-british-columbia"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* API Configuration Validator */}
      <div className="mt-6">
        <ApiConfigValidator
          apiType={formData.api_type}
          apiConfig={formData.api_config}
          onValidationResult={setValidationResult}
        />
      </div>

      {/* Validation Result */}
      {validationResult && (
        <div className={`flex items-center space-x-2 p-3 rounded-md mt-4 ${
          validationResult.valid 
            ? 'text-green-600 bg-green-50' 
            : 'text-red-600 bg-red-50'
        }`}>
          {validationResult.valid ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm">{validationResult.message}</span>
        </div>
      )}
    </div>
  );
}

export default ApiConfigSection;